import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { Ionicons } from "@expo/vector-icons";
import Lottie from "lottie-react";

import Colors from "../../../../Resources/Color";
import GlobalStyles from "../../../../Resources/Styles";
import LoadingBold from "../../../../assets/LoadingBold.json";
import CustomAlert from "../../../Components/CustomAlert";
import TypedConfirmationModal from "../../../Components/TypedConfirmationModal";
import { SyncErrorKind } from "../../../sync/SyncDomainController";
import { useDevice, useScreenDimensions } from "../../../_layout";
import { useToast } from "../../../contexts/ToastContext";
import NutritionEditorActionsMenu from "../../shared/NutritionEditorActionsMenu";
import NutritionDraftStatusPill from "../../shared/NutritionDraftStatusPill";
import NutritionSyncNotice from "../../shared/NutritionSyncNotice";
import { useNutritionDragSensors } from "../../shared/useNutritionDragSensors";
import { COACH_NUTRITION_BOOTSTRAP_RESOURCES } from "../../coachNutritionBootstrap";
import { buildNutritionEditorActionItems } from "../../shared/nutritionEditorActions";
import {
  applyAutoCalculatedEquivalentState,
  buildLinkedEquivalentFromReference,
  getIngredientTotals,
  syncIngredientAutoCalculatedEquivalences,
  updateIngredientQuantityState,
  updateIngredientUnitState as updateNutritionItemUnitState,
} from "../../recipes/recipeLogic";
import { NutritionEntitySyncStatus } from "../../../Firestore/nutrition/OptimisticNutritionEntity";
import { NutritionPlanEditorDetailsSection } from "./NutritionPlanEditorDetailsSection";
import { NutritionPlanMealsSection } from "./NutritionPlanMealsSection";
import { NutritionPlanObjectivesSection } from "./NutritionPlanObjectivesSection";
import { resolveNutritionPlanEditorPersistedSessionState } from "./nutritionPlanEditorViewState";
import {
  createNutritionPlanEditorSession,
  pruneNutritionPlanEditorSession,
  syncNutritionPlanEquivalentPickerSession,
  updateNutritionPlanMealUiState,
  updateNutritionPlanOptionUiState,
} from "./nutritionPlanEditorSession";
import { mealHasPersistedContent } from "./nutritionPlanMealDeleteUtils";
import {
  resolveNextNutritionPlanInlinePickerOwner,
} from "./nutritionPlanFoodPickerScopeUtils";
import {
  buildMealOptionItemsFromEquivalent,
  buildMealOptionItemFromSelectedFood,
  buildMealOptionItemsFromRecipe,
  createEmptyMeal,
  createAddedMealOption,
  createInitialPlanDraft,
  getPlanMealsForSave,
  hasDefinedGoalTargets,
  isPlanDraftSemanticallyEmpty,
  markMealOptionPersistable,
  markPlanMealsEngaged,
  moveMealOptionItem,
  reorderMealOptions,
  reorderMeals,
  setGoalCalories,
  setGoalMode,
  setGoalPercentages,
  setMacroGoalGrams,
} from "../nutritionPlanLogic";
import { getNutritionPlanObjectiveTotals } from "./nutritionPlanObjectivesTotals";
import {
  MAX_MEAL_OPTIONS_PER_MEAL,
  getNutritionLimitMessageDescriptor,
  getNutritionLimitMessageVariables,
  shouldShowNutritionLimitWarning,
  validatePlanDraftLimits,
} from "../../nutritionLimits";
import { downloadNutritionPlanPdf } from "../nutritionPlanPdfExport";
import {
  getNutritionPlanActionErrorDescription,
  resolveNutritionPlanActionSnapshot,
} from "../nutritionPlanActionSnapshot";
import {
  getNutritionPlanDeleteExpectedValue,
  matchesNutritionPlanDeleteConfirmation,
  UNNAMED_NUTRITION_PLAN_DELETE_CONFIRMATION,
} from "../nutritionPlanDeleteUtils";
import {
  getNutritionConflictDescription,
  getNutritionConflictSummary,
} from "../../shared/nutritionConflictWarnings";
import { resolveClientAthleteDisplayName } from "../clientBrowser/clientNutritionCopyToAthleteUtils";

const SectionCard = ({ icon, title, headerRight = null, children, style }) => (
  <View style={[styles.sectionCard, style]}>
    <View style={GlobalStyles.CardHeader}>
      <View style={GlobalStyles.CardHeaderMain}>
        <View style={[GlobalStyles.CardIcon, styles.sectionIconWrap]}>
          <Ionicons name={icon} size={14} color={Colors.grayColor} />
        </View>
        <Text style={[GlobalStyles.CardTitle, styles.sectionTitleUppercase]}>
          {title}
        </Text>
      </View>
      {headerRight ? <View style={styles.sectionHeaderRight}>{headerRight}</View> : null}
    </View>
    {children}
  </View>
);

export const NutritionPlanEditorView = observer(function NutritionPlanEditorView({
  coach,
  client = null,
  planStore = null,
  watchPlan = null,
  planEntity,
  planRootRecord = null,
  isEditing,
  onBack,
  onAssign,
  onDuplicate,
  onDelete,
}) {
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();
  const { screenSize } = useScreenDimensions();
  const { isMobile } = useDevice();
  const language = i18n?.resolvedLanguage || i18n?.language || "en";
  const [isBackButtonHovered, setIsBackButtonHovered] = useState(false);
  const [isEditGoalsHovered, setIsEditGoalsHovered] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [deleteModalState, setDeleteModalState] = useState({
    isVisible: false,
    typedValue: "",
  });
  const [pendingDeleteMealId, setPendingDeleteMealId] = useState(null);
  const [openInlinePickerOwner, setOpenInlinePickerOwner] = useState(null);
  const [selectedMealOptionIds, setSelectedMealOptionIds] = useState({});
  const [editorSession, setEditorSession] = useState(() =>
    createNutritionPlanEditorSession(),
  );
  const editorScrollRef = useRef(null);
  const isCompactLayout = screenSize === "small";
  const compactRowLayout = screenSize !== "large";
  const hasEditorActions = !isPlanDraftSemanticallyEmpty(
    planEntity?.getDraftSnapshot?.() ?? planEntity?.draft,
  );
  const topBarMenuItems = useMemo(
    () =>
      buildNutritionEditorActionItems({
        t,
        syncStatus: planEntity?.syncStatus,
        includeDuplicate: hasEditorActions && typeof onDuplicate === "function",
        includeDelete: hasEditorActions && typeof onDelete === "function",
        includeExportPdf: true,
        includeAssign: typeof onAssign === "function",
        isExportingPdf,
      }),
    [
      hasEditorActions,
      isExportingPdf,
      onAssign,
      onDelete,
      onDuplicate,
      planEntity?.syncStatus,
      t,
    ],
  );
  const sensors = useNutritionDragSensors();
  const { shouldWatchPersistedPlan, isMealsLoading } =
    resolveNutritionPlanEditorPersistedSessionState({
      planId: planEntity?.id,
      isEditing,
      mealsHydrated: planEntity?.mealsHydrated,
    });
  const watchPersistedPlan = useMemo(() => {
    if (typeof watchPlan === "function") {
      return watchPlan;
    }

    if (typeof planStore?.watchPlan === "function") {
      return planStore.watchPlan;
    }

    if (typeof coach?.watchNutritionPlan === "function") {
      return coach.watchNutritionPlan.bind(coach);
    }

    return null;
  }, [coach, planStore, watchPlan]);

  useEffect(() => {
    if (typeof watchPersistedPlan !== "function" || !shouldWatchPersistedPlan) {
      return undefined;
    }

    let isActive = true;
    let unsubscribe = null;

    watchPersistedPlan(planEntity.id, () => {
      if (!isActive) {
        return;
      }
    })
      .then((nextUnsubscribe) => {
        if (!isActive) {
          nextUnsubscribe?.();
          return;
        }

        unsubscribe = nextUnsubscribe;
      })
      .catch(() => {});

    return () => {
      isActive = false;
      unsubscribe?.();
    };
  }, [planEntity?.id, shouldWatchPersistedPlan, watchPersistedPlan]);

  useEffect(() => {
    coach?.ensureNutritionResourcesFromCloud?.(
      COACH_NUTRITION_BOOTSTRAP_RESOURCES.nutritionPlanEditor,
    )?.catch?.(() => {});
  }, [coach]);

  const draft =
    planEntity?.draft ??
    createInitialPlanDraft({}, {
      language,
      t,
    });
  const pendingDeleteMeal =
    draft.meals.find((meal) => meal.id === pendingDeleteMealId) ?? null;
  const objectiveTotals = useMemo(
    () => getNutritionPlanObjectiveTotals(draft, selectedMealOptionIds),
    [draft, selectedMealOptionIds],
  );
  const objectivesHaveGoals = useMemo(
    () => hasDefinedGoalTargets(draft.goals),
    [draft.goals],
  );
  const [isEditingObjectives, setIsEditingObjectives] = useState(
    () => !objectivesHaveGoals,
  );
  const shouldInlineObjectivesOnCompact = isCompactLayout;
  const deletePlanExpectedValue = getNutritionPlanDeleteExpectedValue(
    planRootRecord,
    planEntity,
    planEntity?.getDraftSnapshot?.(),
  );
  const isDeletePlanConfirmEnabled = matchesNutritionPlanDeleteConfirmation({
    input: deleteModalState.typedValue,
    expectedValue: deletePlanExpectedValue,
  });

  useEffect(() => {
    if (!objectivesHaveGoals) {
      setIsEditingObjectives(true);
    }
  }, [objectivesHaveGoals]);

  useEffect(() => {
    if (pendingDeleteMealId && !pendingDeleteMeal) {
      setPendingDeleteMealId(null);
    }
  }, [pendingDeleteMeal, pendingDeleteMealId]);

  useEffect(() => {
    setEditorSession(createNutritionPlanEditorSession());
    setOpenInlinePickerOwner(null);
    setSelectedMealOptionIds({});
    setPendingDeleteMealId(null);
  }, [planEntity?.id]);

  useEffect(() => {
    setSelectedMealOptionIds((current) => {
      const meals = Array.isArray(draft?.meals) ? draft.meals : [];
      const next = {};

      meals.forEach((meal) => {
        const mealId = String(meal?.id ?? "").trim();
        if (!mealId) {
          return;
        }

        const currentSelection = String(current?.[mealId] ?? "").trim();
        if (!currentSelection) {
          return;
        }

        const hasSelectedOption = Array.isArray(meal?.options)
          ? meal.options.some((option) => option?.id === currentSelection)
          : false;
        if (hasSelectedOption) {
          next[mealId] = currentSelection;
        }
      });

      const currentKeys = Object.keys(current);
      const nextKeys = Object.keys(next);
      if (
        currentKeys.length === nextKeys.length &&
        currentKeys.every((key) => current[key] === next[key])
      ) {
        return current;
      }

      return next;
    });
  }, [draft?.meals]);

  useEffect(() => {
    setOpenInlinePickerOwner((currentOwner) => {
      const normalizedMealId = String(currentOwner?.mealId ?? "").trim();
      if (!normalizedMealId) {
        return null;
      }

      const hasOpenMeal = Array.isArray(draft?.meals)
        ? draft.meals.some((meal) => meal?.id === normalizedMealId)
        : false;

      return hasOpenMeal ? currentOwner : null;
    });
  }, [draft?.meals]);

  useEffect(() => {
    setEditorSession((currentSession) =>
      pruneNutritionPlanEditorSession(currentSession, draft?.meals),
    );
  }, [draft?.meals]);

  useEffect(() => {
    setEditorSession((currentSession) =>
      syncNutritionPlanEquivalentPickerSession(
        currentSession,
        openInlinePickerOwner,
      ),
    );
  }, [openInlinePickerOwner]);

  const applyDraftUpdate = useCallback(
    (updater, options = {}) => {
      if (!planEntity) {
        return;
      }

      const currentDraft = planEntity.getDraftSnapshot();
      const nextDraft =
        typeof updater === "function" ? updater(currentDraft) : updater;
      planEntity.applyDraft(nextDraft, options);
    },
    [planEntity],
  );

  const updateField = useCallback(
    (field, value) => {
      applyDraftUpdate((current) => ({
        ...current,
        [field]: value,
      }));
    },
    [applyDraftUpdate],
  );

  const updateMeals = useCallback(
    (nextMeals) => {
      applyDraftUpdate((current) =>
        markPlanMealsEngaged({
          ...current,
          meals: reorderMeals(nextMeals),
        }),
      );
    },
    [applyDraftUpdate],
  );

  const applyMealsDraftUpdate = useCallback(
    (updater, options = {}) => {
      applyDraftUpdate(
        (current) =>
          markPlanMealsEngaged(
            typeof updater === "function" ? updater(current) : updater,
          ),
        options,
      );
    },
    [applyDraftUpdate],
  );

  const handleSelectMealOption = useCallback((mealId, optionId) => {
    const normalizedMealId = String(mealId ?? "").trim();
    if (!normalizedMealId) {
      return;
    }

    const normalizedOptionId = String(optionId ?? "").trim();
    setSelectedMealOptionIds((current) => {
      if (!normalizedOptionId) {
        if (!Object.prototype.hasOwnProperty.call(current, normalizedMealId)) {
          return current;
        }

        const next = { ...current };
        delete next[normalizedMealId];
        return next;
      }

      if (current[normalizedMealId] === normalizedOptionId) {
        return current;
      }

      return {
        ...current,
        [normalizedMealId]: normalizedOptionId,
      };
    });
  }, []);

  const handleInlinePickerOpenChange = useCallback((requestedOwner, nextOpen) => {
    setOpenInlinePickerOwner((currentOwner) =>
      resolveNextNutritionPlanInlinePickerOwner({
        currentOwner,
        requestedOwner,
        nextOpen,
      }),
    );
  }, []);

  const handleUpdateMealUiState = useCallback((meal, updater) => {
    setEditorSession((currentSession) =>
      updateNutritionPlanMealUiState(currentSession, meal, updater),
    );
  }, []);

  const handleUpdateOptionUiState = useCallback((mealId, optionId, updater) => {
    setEditorSession((currentSession) =>
      updateNutritionPlanOptionUiState(currentSession, { mealId, optionId }, updater),
    );
  }, []);

  const showLimitWarningToast = useCallback(
    (limitsValidation) => {
      if (!shouldShowNutritionLimitWarning(limitsValidation)) {
        return;
      }

      const descriptor = getNutritionLimitMessageDescriptor(limitsValidation);
      showToast(descriptor?.title ?? "nutritionLimitReachedTitle-string", {
        type: "warning",
        description:
          descriptor?.description ?? "nutritionLimitReachedDescription-string",
        variables: getNutritionLimitMessageVariables(limitsValidation),
      });
    },
    [showToast],
  );

  const addMeal = useCallback(() => {
    const currentDraft = planEntity?.getDraftSnapshot?.();
    if (!currentDraft) {
      return;
    }

    const nextDraft = markPlanMealsEngaged({
      ...currentDraft,
      meals: [
        ...currentDraft.meals,
        createEmptyMeal(currentDraft.meals.length, currentDraft.meals, {
          language,
          t,
          persistInitialOption: true,
        }),
      ],
    });
    const limitsValidation = validatePlanDraftLimits({
      name: nextDraft.name,
      notes: nextDraft.notes,
      meals: getPlanMealsForSave(nextDraft),
    });
    if (!limitsValidation.ok) {
      showLimitWarningToast(limitsValidation);
      return;
    }

    planEntity.applyDraft(nextDraft);
  }, [language, planEntity, showLimitWarningToast, t]);

  const updateMealName = useCallback(
    (mealId, value) => {
      applyMealsDraftUpdate((current) => ({
        ...current,
        meals: current.meals.map((meal) =>
          meal.id === mealId
            ? {
                ...meal,
                name: value,
              }
            : meal,
        ),
      }));
    },
    [applyMealsDraftUpdate],
  );

  const updateMealNotes = useCallback(
    (mealId, value) => {
      applyMealsDraftUpdate((current) => ({
        ...current,
        meals: current.meals.map((meal) =>
          meal.id === mealId
            ? {
                ...meal,
                notes: value,
              }
            : meal,
        ),
      }));
    },
    [applyMealsDraftUpdate],
  );

  const removeMealNow = useCallback(
    (mealId) => {
      applyMealsDraftUpdate((current) => ({
        ...current,
        meals: current.meals.filter((meal) => meal.id !== mealId),
      }));
    },
    [applyMealsDraftUpdate],
  );

  const requestRemoveMeal = useCallback(
    (mealId) => {
      const currentDraft = planEntity?.getDraftSnapshot?.();
      if (!currentDraft) {
        return;
      }

      const meal = currentDraft.meals.find((entry) => entry.id === mealId);
      if (!meal) {
        return;
      }

      if (mealHasPersistedContent(meal)) {
        setPendingDeleteMealId(mealId);
        return;
      }

      removeMealNow(mealId);
    },
    [planEntity, removeMealNow],
  );

  const addMealOption = useCallback(
    (mealId) => {
      const currentDraft = planEntity?.getDraftSnapshot?.();
      if (!currentDraft) {
        return false;
      }
      const currentMeal = currentDraft.meals.find((meal) => meal.id === mealId);
      if (
        Array.isArray(currentMeal?.options) &&
        currentMeal.options.length >= MAX_MEAL_OPTIONS_PER_MEAL
      ) {
        showLimitWarningToast({
          ok: false,
          code: "meal_options_limit",
          meta: {
            limit: MAX_MEAL_OPTIONS_PER_MEAL,
            count: currentMeal.options.length,
            mealId,
          },
        });
        return false;
      }

      const nextDraft = markPlanMealsEngaged({
        ...currentDraft,
        meals: currentDraft.meals.map((meal) =>
          meal.id === mealId
            ? {
                ...meal,
                options: [
                  ...meal.options,
                  createAddedMealOption(meal.options.length, {
                    language,
                  }),
                ],
              }
            : meal,
        ),
      });
      const limitsValidation = validatePlanDraftLimits({
        name: nextDraft.name,
        notes: nextDraft.notes,
        meals: getPlanMealsForSave(nextDraft),
      });
      if (!limitsValidation.ok) {
        showLimitWarningToast(limitsValidation);
        return false;
      }

      planEntity.applyDraft(nextDraft);
      return true;
    },
    [planEntity, showLimitWarningToast],
  );

  const reorderOptions = useCallback(
    (mealId, nextOptions) => {
      applyMealsDraftUpdate((current) => ({
        ...current,
        meals: reorderMealOptions(current.meals, mealId, nextOptions),
      }));
    },
    [applyMealsDraftUpdate],
  );

  const moveOptionItem = useCallback(
    ({
      fromMealId,
      fromOptionId,
      itemId,
      toMealId,
      toOptionId,
      overItemId = null,
      insertAfterOverItem = false,
    }) => {
      applyMealsDraftUpdate((current) => ({
        ...current,
        meals: moveMealOptionItem(current.meals, {
          fromMealId,
          fromOptionId,
          itemId,
          toMealId,
          toOptionId,
          overItemId,
          insertAfterOverItem,
        }),
      }));
    },
    [applyMealsDraftUpdate],
  );

  const updateOptionName = useCallback(
    (mealId, optionId, value) => {
      applyMealsDraftUpdate((current) => ({
        ...current,
        meals: current.meals.map((meal) =>
          meal.id === mealId
            ? {
                ...meal,
                options: meal.options.map((option) =>
                  option.id === optionId
                    ? {
                        ...option,
                        name: value,
                      }
                    : option,
                ),
              }
            : meal,
        ),
      }));
    },
    [applyMealsDraftUpdate],
  );

  const removeOption = useCallback(
    (mealId, optionId) => {
      applyMealsDraftUpdate((current) => ({
        ...current,
        meals: current.meals.map((meal) =>
          meal.id === mealId
            ? {
                ...meal,
                options: meal.options.filter((option) => option.id !== optionId),
              }
            : meal,
        ),
      }));
    },
    [applyMealsDraftUpdate],
  );

  const addFoodToOption = useCallback(
    (mealId, { optionId, selection, parentItemId = null }) => {
      const currentDraft = planEntity?.getDraftSnapshot?.();
      if (!currentDraft) {
        return;
      }

      let conflictSummary = null;
      let nextItems = [];

      if (parentItemId) {
        const referenceItem = currentDraft.meals
          .find((meal) => meal.id === mealId)
          ?.options.find((option) => option.id === optionId)
          ?.items.find((item) => item.id === parentItemId);
        const nextItem = buildMealOptionItemFromSelectedFood(
          selection?.sourceRecord ?? selection,
          language,
          t,
        );

        nextItems =
          referenceItem && nextItem
            ? [buildLinkedEquivalentFromReference(referenceItem, nextItem)]
            : [];
      } else if (selection?.entityKind === "equivalent") {
        nextItems = buildMealOptionItemsFromEquivalent(selection?.sourceRecord);
      } else if (selection?.entityKind === "recipe") {
        nextItems = buildMealOptionItemsFromRecipe(selection?.sourceRecord);
      } else {
        nextItems = [
          buildMealOptionItemFromSelectedFood(
            selection?.sourceRecord ?? selection,
            language,
            t,
          ),
        ].filter(Boolean);
      }

      if (nextItems.length === 0) {
        return;
      }

      const nextDraft = markPlanMealsEngaged({
        ...currentDraft,
        meals: currentDraft.meals.map((meal) =>
          meal.id === mealId
            ? {
                ...meal,
                options: meal.options.map((option) => {
                  if (option.id !== optionId) {
                    return option;
                  }

                  const nextOption = markMealOptionPersistable(option);
                  if (!parentItemId) {
                    return {
                      ...nextOption,
                      items: [...option.items, ...nextItems],
                    };
                  }

                  return {
                    ...nextOption,
                    items: option.items.map((item) =>
                      item.id === parentItemId
                        ? {
                            ...item,
                            equivalences: [
                              ...(Array.isArray(item.equivalences)
                                ? item.equivalences
                                : []),
                              ...nextItems,
                            ],
                          }
                        : item,
                    ),
                  };
                }),
              }
            : meal,
        ),
      });
      const limitsValidation = validatePlanDraftLimits({
        name: nextDraft.name,
        notes: nextDraft.notes,
        meals: getPlanMealsForSave(nextDraft),
      });
      if (!limitsValidation.ok) {
        showLimitWarningToast(limitsValidation);
        return;
      }

      conflictSummary = getNutritionConflictSummary({
        client,
        source: {
          items: nextItems,
        },
        t,
      });

      planEntity.applyDraft(nextDraft);

      if (conflictSummary?.hasConflicts) {
        showToast(t("nutritionConflictWarningToastTitle-string", "Conflict warning"), {
          type: "warning",
          description:
            getNutritionConflictDescription(conflictSummary) ||
            t(
              "nutritionConflictWarningToastDescription-string",
              "This item conflicts with the athlete nutrition settings.",
            ),
        });
      }
    },
    [
      applyDraftUpdate,
      client,
      language,
      planEntity,
      showToast,
      showLimitWarningToast,
      t,
      buildMealOptionItemsFromEquivalent,
      buildMealOptionItemsFromRecipe,
    ],
  );

  const updateSelectedItemQuantity = useCallback(
    (mealId, optionId, itemId, value) => {
      applyMealsDraftUpdate((current) => ({
        ...current,
        meals: current.meals.map((meal) =>
          meal.id === mealId
            ? {
                ...meal,
                options: meal.options.map((option) =>
                      option.id === optionId
                        ? {
                            ...option,
                            items: option.items.map((item) =>
                              item.id === itemId
                                ? syncIngredientAutoCalculatedEquivalences(
                                    updateIngredientQuantityState(item, value),
                                  )
                                : item,
                            ),
                          }
                    : option,
                ),
              }
            : meal,
        ),
      }));
    },
    [applyMealsDraftUpdate],
  );

  const changeSelectedItemUnit = useCallback(
    (mealId, optionId, itemId, nextUnitKey) => {
      applyMealsDraftUpdate((current) => ({
        ...current,
        meals: current.meals.map((meal) =>
          meal.id === mealId
            ? {
                ...meal,
                options: meal.options.map((option) =>
                      option.id === optionId
                        ? {
                            ...option,
                            items: option.items.map((item) =>
                              item.id === itemId
                                ? syncIngredientAutoCalculatedEquivalences(
                                    updateNutritionItemUnitState(item, nextUnitKey),
                                  )
                                : item,
                            ),
                          }
                    : option,
                ),
              }
            : meal,
        ),
      }));
    },
    [applyMealsDraftUpdate],
  );

  const updateSelectedEquivalenceQuantity = useCallback(
    (mealId, optionId, itemId, equivalenceId, value) => {
      applyMealsDraftUpdate((current) => ({
        ...current,
        meals: current.meals.map((meal) =>
          meal.id === mealId
            ? {
                ...meal,
                options: meal.options.map((option) =>
                  option.id === optionId
                    ? {
                        ...option,
                        items: option.items.map((item) =>
                          item.id === itemId
                            ? {
                                ...item,
                                equivalences: (item.equivalences ?? []).map(
                                  (equivalence) =>
                                    equivalence.id === equivalenceId
                                      ? updateIngredientQuantityState(
                                          equivalence,
                                          value,
                                        )
                                      : equivalence,
                                ),
                              }
                            : item,
                        ),
                      }
                    : option,
                ),
              }
            : meal,
        ),
      }));
    },
    [applyMealsDraftUpdate],
  );

  const changeSelectedEquivalenceUnit = useCallback(
    (mealId, optionId, itemId, equivalenceId, nextUnitKey) => {
      applyMealsDraftUpdate((current) => ({
        ...current,
        meals: current.meals.map((meal) =>
          meal.id === mealId
            ? {
                ...meal,
                options: meal.options.map((option) =>
                  option.id === optionId
                    ? {
                        ...option,
                        items: option.items.map((item) =>
                          item.id === itemId
                            ? {
                                ...item,
                                equivalences: (item.equivalences ?? []).map(
                                  (equivalence) =>
                                    equivalence.id === equivalenceId
                                      ? applyAutoCalculatedEquivalentState(
                                          equivalence,
                                          getIngredientTotals(item).calories,
                                          nextUnitKey,
                                        )
                                      : equivalence,
                                ),
                              }
                            : item,
                        ),
                      }
                    : option,
                ),
              }
            : meal,
        ),
      }));
    },
    [applyMealsDraftUpdate],
  );

  const removeOptionItem = useCallback(
    (mealId, optionId, itemId) => {
      applyMealsDraftUpdate((current) => ({
        ...current,
        meals: current.meals.map((meal) =>
          meal.id === mealId
            ? {
                ...meal,
                options: meal.options.map((option) =>
                  option.id === optionId
                    ? {
                        ...markMealOptionPersistable(option),
                        items: option.items.filter((item) => item.id !== itemId),
                      }
                    : option,
                ),
              }
            : meal,
        ),
      }));
    },
    [applyMealsDraftUpdate],
  );

  const removeOptionEquivalence = useCallback(
    (mealId, optionId, itemId, equivalenceId) => {
      applyMealsDraftUpdate((current) => ({
        ...current,
        meals: current.meals.map((meal) =>
          meal.id === mealId
            ? {
                ...meal,
                options: meal.options.map((option) =>
                  option.id === optionId
                    ? {
                        ...option,
                        items: option.items.map((item) =>
                          item.id === itemId
                            ? {
                                ...item,
                                equivalences: (item.equivalences ?? []).filter(
                                  (equivalence) => equivalence.id !== equivalenceId,
                                ),
                              }
                            : item,
                        ),
                      }
                    : option,
                ),
              }
            : meal,
        ),
      }));
    },
    [applyMealsDraftUpdate],
  );

  const handleCalorieGoalChange = useCallback(
    (value) => {
      applyDraftUpdate((current) => ({
        ...current,
        goals: setGoalCalories(current.goals, value),
      }));
    },
    [applyDraftUpdate],
  );

  const handleMacroPercentagesChange = useCallback(
    (nextPercentages) => {
      applyDraftUpdate((current) => ({
        ...current,
        goals: setGoalPercentages(current.goals, nextPercentages),
      }));
    },
    [applyDraftUpdate],
  );

  const handleMacroGoalGramsChange = useCallback(
    (field, value) => {
      applyDraftUpdate((current) => ({
        ...current,
        goals: setMacroGoalGrams(current.goals, field, value),
      }));
    },
    [applyDraftUpdate],
  );

  const handleGoalModeChange = useCallback(
    (nextMode) => {
      applyDraftUpdate((current) => ({
        ...current,
        goals: setGoalMode(current.goals, nextMode),
      }));
    },
    [applyDraftUpdate],
  );

  const handleRetrySave = useCallback(() => {
    planEntity?.saveNow?.();
  }, [planEntity]);

  const syncNotice = useMemo(() => {
    if (
      !planEntity?.lastError ||
      planEntity.syncStatus === NutritionEntitySyncStatus.OFFLINE_PENDING
    ) {
      return null;
    }

    if (planEntity.errorKind === SyncErrorKind.BLOCKED) {
      return {
        tone: "warning",
        title: t(
          "nutritionPlanSaveConflictTitle-string",
          "This plan changed elsewhere",
        ),
        message: t(
          "nutritionPlanAutosaveConflictMessage-string",
          "We rebased untouched fields onto the latest plan revision and will retry saving your local changes.",
        ),
      };
    }

    return {
      tone:
        planEntity.syncStatus === NutritionEntitySyncStatus.ERROR
          ? "error"
          : "warning",
      title: t("autosaveError-string"),
      message: t(
        "nutritionEditorRetryNotice-string",
        "We couldn't save the latest changes. Try saving again.",
      ),
      actionLabel: t("save-string"),
      onAction: handleRetrySave,
    };
  }, [
    handleRetrySave,
    planEntity?.errorKind,
    planEntity?.lastError,
    planEntity?.syncStatus,
    t,
  ]);

  const handleBack = useCallback(() => {
    onBack?.();
  }, [onBack]);

  const handleExportPdf = useCallback(async () => {
    if (!planEntity || isExportingPdf) {
      return;
    }

    setIsExportingPdf(true);

    try {
      const resolvedPlanStore = planStore ?? planEntity?.entityStore ?? null;
      const sourceRootRecord =
        planRootRecord ??
        resolvedPlanStore?.planRootRecordsById?.get?.(String(planEntity?.id ?? "").trim().toLowerCase()) ??
        null;
      const { planRecord } = await resolveNutritionPlanActionSnapshot({
        planId: planEntity.id,
        sourceEntity: planEntity,
        sourceRootRecord,
        fallbackPlan: planEntity?.getDraftSnapshot?.() ?? planEntity,
        loadMealRecords: (resolvedPlanId, options = {}) =>
          resolvedPlanStore?.loadPlanMealRecords?.(resolvedPlanId, options) ?? Promise.resolve([]),
        language,
      });
      const downloadedFileName = await downloadNutritionPlanPdf({
        draft: planRecord,
        rootRecord: sourceRootRecord,
        coach,
        athleteName: client ? resolveClientAthleteDisplayName(client) : "",
        language,
      });

      showToast(t("nutritionPlanPdfExportedTitle-string", "PDF exported"), {
        type: "info",
        title: t("nutritionPlanPdfExportedTitle-string", "PDF exported"),
        description: t(
          "nutritionPlanPdfExportedDescription-string",
          'Downloaded "{{name}}".',
          { name: downloadedFileName },
        ),
      });
    } catch (error) {
      console.error("Failed to export nutrition plan PDF:", error);
      showToast(t("nutritionPlanPdfExportErrorTitle-string", "Could not export PDF"), {
        type: "error",
        title: t("nutritionPlanPdfExportErrorTitle-string", "Could not export PDF"),
        description: getNutritionPlanActionErrorDescription(
          error,
          t("nutritionPlanPdfExportErrorDescription-string", "Try again in a moment."),
          t,
        ),
      });
    } finally {
      setIsExportingPdf(false);
    }
  }, [client, coach, isExportingPdf, language, planEntity, planStore, showToast, t]);
  const handleEditorAction = useCallback(
    (actionKey) => {
      if (actionKey === "exportPdf") {
        handleExportPdf();
        return;
      }

      if (actionKey === "assign") {
        onAssign?.(planEntity);
        return;
      }

      if (actionKey === "duplicate") {
        onDuplicate?.();
        return;
      }

      if (actionKey === "delete") {
        setDeleteModalState({
          isVisible: true,
          typedValue: "",
        });
      }
    },
    [handleExportPdf, onAssign, onDuplicate, planEntity],
  );
  const handleCloseDeleteModal = useCallback(() => {
    setDeleteModalState({
      isVisible: false,
      typedValue: "",
    });
  }, []);
  const handleCloseDeleteMealAlert = useCallback(() => {
    setPendingDeleteMealId(null);
  }, []);
  const handleConfirmDelete = useCallback(async () => {
    if (typeof onDelete !== "function" || !isDeletePlanConfirmEnabled) {
      return;
    }

    try {
      await onDelete(planEntity);
    } catch (_error) {
      // Keep the modal open. The parent handler owns user-facing error feedback.
    }
  }, [isDeletePlanConfirmEnabled, onDelete, planEntity]);
  const handleConfirmDeleteMeal = useCallback(() => {
    if (!pendingDeleteMealId) {
      return;
    }

    removeMealNow(pendingDeleteMealId);
  }, [pendingDeleteMealId, removeMealNow]);

  if (!planEntity) {
    return null;
  }

  const objectivesCard = (
    <SectionCard
      icon="analytics-outline"
      title={t("nutritionPlanObjectivesTitle-string", "Objectives")}
      headerRight={
        !isEditingObjectives ? (
          <TouchableOpacity
            activeOpacity={0.82}
            onPress={() => {
              setIsEditGoalsHovered(false);
              setIsEditingObjectives(true);
            }}
            onMouseEnter={!isMobile ? () => setIsEditGoalsHovered(true) : undefined}
            onMouseLeave={!isMobile ? () => setIsEditGoalsHovered(false) : undefined}
            style={[
              styles.objectivesHeaderAction,
              isEditGoalsHovered ? styles.objectivesHeaderActionHovered : null,
            ]}
          >
            <Text style={styles.objectivesHeaderActionText}>
              {t("nutritionPlanEditGoals-string", "Edit goals")}
            </Text>
          </TouchableOpacity>
        ) : null
      }
    >
      <NutritionPlanObjectivesSection
        t={t}
        draftGoals={draft.goals}
        currentTotals={objectiveTotals}
        loadingFallbackTotals={planEntity?.summary_totals ?? null}
        isCurrentTotalsLoading={isMealsLoading}
        isEditingGoals={isEditingObjectives}
        onDoneEditing={() => setIsEditingObjectives(false)}
        onCalorieChange={handleCalorieGoalChange}
        onMacroPercentagesChange={handleMacroPercentagesChange}
        onMacroGramsChange={handleMacroGoalGramsChange}
        onGoalModeChange={handleGoalModeChange}
      />
    </SectionCard>
  );

  return (
    <ScrollView
      ref={editorScrollRef}
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
      keyboardShouldPersistTaps="handled"
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.topBar}>
        <TouchableOpacity
          activeOpacity={0.8}
          onPress={handleBack}
          onMouseEnter={!isMobile ? () => setIsBackButtonHovered(true) : undefined}
          onMouseLeave={!isMobile ? () => setIsBackButtonHovered(false) : undefined}
          style={[
            styles.backButton,
            isBackButtonHovered ? styles.backButtonHover : null,
          ]}
        >
          <Ionicons name="arrow-back" size={15} color={Colors.bubbleColor} />
          <Text style={styles.backButtonText}>{t("back-string")}</Text>
        </TouchableOpacity>

        <View style={styles.topBarActions}>
          <NutritionDraftStatusPill session={planEntity} />
          <NutritionEditorActionsMenu
            items={topBarMenuItems}
            onAction={handleEditorAction}
          />
        </View>
      </View>

      <NutritionSyncNotice
        session={planEntity}
        tone={syncNotice?.tone}
        title={syncNotice?.title}
        message={syncNotice?.message}
        actionLabel={syncNotice?.actionLabel}
        onAction={syncNotice?.onAction}
        onRetry={handleRetrySave}
        style={styles.syncNotice}
      />

      <View
        style={[
          styles.editorColumns,
          isCompactLayout ? styles.editorColumnsCompact : null,
        ]}
      >
        <View
          style={[
            styles.editorMainColumn,
            isCompactLayout ? styles.editorMainColumnCompact : null,
          ]}
        >
          <View
            style={[
              styles.editorMainColumnSections,
              isCompactLayout ? styles.editorMainColumnSectionsCompact : null,
            ]}
          >
            <SectionCard
              icon="reader-outline"
              title={
                isEditing
                  ? t("editNutritionPlan-string", "Edit nutrition plan")
                  : t("createNutritionPlan-string", "Create nutrition plan")
              }
            >
              <NutritionPlanEditorDetailsSection
                t={t}
                draft={draft}
                updateField={updateField}
              />
            </SectionCard>

            {shouldInlineObjectivesOnCompact ? objectivesCard : null}

            {isMealsLoading ? (
              <View style={styles.loadingState}>
                <Lottie animationData={LoadingBold} loop style={styles.loadingIcon} />
                <Text style={styles.loadingText}>
                  {t("nutritionPlanMealsLoading-string", "Loading meals...")}
                </Text>
              </View>
            ) : null}

            <NutritionPlanMealsSection
              coach={coach}
              client={client}
              t={t}
              language={language}
              meals={draft.meals}
              openInlinePickerOwner={openInlinePickerOwner}
              onInlinePickerOpenChange={handleInlinePickerOpenChange}
              selectedMealOptionIds={selectedMealOptionIds}
              onSelectMealOption={handleSelectMealOption}
              mealUiById={editorSession.mealUiById}
              optionUiById={editorSession.optionUiById}
              onUpdateMealUiState={handleUpdateMealUiState}
              onUpdateOptionUiState={handleUpdateOptionUiState}
              compactLayout={compactRowLayout}
              sensors={sensors}
              editorScrollRef={editorScrollRef}
              addMeal={addMeal}
              reorderMeals={updateMeals}
              updateMealName={updateMealName}
              updateMealNotes={updateMealNotes}
              requestRemoveMeal={requestRemoveMeal}
              addOption={addMealOption}
              reorderOptions={reorderOptions}
              updateOptionName={updateOptionName}
              removeOption={removeOption}
              onAddFood={addFoodToOption}
              moveOptionItem={moveOptionItem}
              updateItemQuantity={updateSelectedItemQuantity}
              updateItemUnit={changeSelectedItemUnit}
              removeItem={removeOptionItem}
              updateEquivalenceQuantity={updateSelectedEquivalenceQuantity}
              updateEquivalenceUnit={changeSelectedEquivalenceUnit}
              removeEquivalence={removeOptionEquivalence}
              isLoading={isMealsLoading}
            />
          </View>
        </View>

        {!shouldInlineObjectivesOnCompact ? (
          <View
            style={[
              styles.editorSummaryColumn,
              isCompactLayout ? styles.editorSummaryColumnCompact : null,
            ]}
          >
            {objectivesCard}
          </View>
        ) : null}
      </View>

      {pendingDeleteMeal ? (
        <CustomAlert
          isVisible={true}
          onClose={handleCloseDeleteMealAlert}
          title={t(
            "nutritionPlanDeleteMeal-string",
            "Delete meal",
          ).toUpperCase()}
          text="nutritionPlanDeleteMealAlertText-string"
          action={handleConfirmDeleteMeal}
          specialCTA="delete-string"
        />
      ) : null}

      <TypedConfirmationModal
        isVisible={deleteModalState.isVisible}
        onClose={handleCloseDeleteModal}
        title={t(
          "nutritionPlanDeleteConfirmationTitle-string",
          "DELETE NUTRITION PLAN",
        )}
        description={t(
          "nutritionPlanDeleteConfirmationDescription-string",
          "This will permanently delete the nutrition plan. This action cannot be undone.",
        )}
        instruction={t(
          "nutritionPlanDeleteConfirmationInstruction-string",
          'Type "{{value}}" to confirm.',
          {
            value:
              deletePlanExpectedValue === UNNAMED_NUTRITION_PLAN_DELETE_CONFIRMATION
                ? UNNAMED_NUTRITION_PLAN_DELETE_CONFIRMATION
                : deletePlanExpectedValue,
          },
        )}
        placeholder={
          deletePlanExpectedValue === UNNAMED_NUTRITION_PLAN_DELETE_CONFIRMATION
            ? UNNAMED_NUTRITION_PLAN_DELETE_CONFIRMATION
            : t(
                "nutritionPlanDeleteConfirmationPlaceholder-string",
                "Type nutrition plan name",
              )
        }
        value={deleteModalState.typedValue}
        onChangeText={(typedValue) =>
          setDeleteModalState((previousState) => ({
            ...previousState,
            typedValue,
          }))
        }
        onConfirm={handleConfirmDelete}
        isConfirmEnabled={isDeletePlanConfirmEnabled}
        confirmLabel={t("delete-string", "Delete")}
        cancelLabel={t("cancel-string", "Cancel")}
      />
    </ScrollView>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: "100%",
    marginTop: 24,
  },
  contentContainer: {
    paddingBottom: 36,
  },
  editorColumns: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 16,
  },
  editorColumnsCompact: {
    flexDirection: "column",
    alignItems: "stretch",
    gap: 12,
  },
  editorMainColumn: {
    flex: 1,
    minWidth: 0,
  },
  editorMainColumnCompact: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: "auto",
    width: "100%",
    alignSelf: "stretch",
  },
  editorMainColumnSections: {
    gap: 16,
  },
  editorMainColumnSectionsCompact: {
    gap: 12,
  },
  editorSummaryColumn: {
    width: 360,
    maxWidth: "40%",
  },
  editorSummaryColumnCompact: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: "auto",
    width: "100%",
    maxWidth: "100%",
    alignSelf: "stretch",
  },
  sectionHeaderRight: {
    marginLeft: 12,
  },
  objectivesHeaderAction: {
    paddingVertical: 4,
  },
  objectivesHeaderActionHovered: {
    opacity: 0.7,
  },
  objectivesHeaderActionText: {
    fontSize: 12,
    fontWeight: "600",
    color: Colors.bubbleColor,
  },
  topBar: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 12,
  },
  backButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 10,
    transition: "opacity 180ms ease-in-out",
  },
  backButtonHover: {
    opacity: 0.8,
  },
  backButtonText: {
    fontSize: 13,
    fontWeight: "500",
    color: Colors.bubbleColor,
  },
  topBarActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  syncNotice: {
    marginBottom: 18,
  },
  sectionCard: {
    backgroundColor: Colors.whiteColor,
    borderRadius: 14,
    borderWidth: 0.5,
    borderColor: Colors.borderColor,
    padding: 18,
  },
  sectionTitleUppercase: {
    textTransform: "uppercase",
  },
  sectionIconWrap: {
    width: 30,
    height: 30,
    alignItems: "center",
    justifyContent: "center",
  },
  loadingState: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    paddingHorizontal: 4,
    paddingVertical: 2,
  },
  loadingText: {
    fontSize: 13,
    color: Colors.darkgrayColor,
    fontWeight: "500",
  },
  loadingIcon: {
    width: 18,
    height: 18,
  },
});
